<template>
  <layout-header />
  <div class="flex flex-col gap-20 p-10 w-full container mx-auto">
    <div class="flex flex-col gap-5">
      <div class="flex flex-row gap-5 justify-end">
        <AreaCard
          :fetch-list="fetchAreas"
          :loading="areasLoading"
        />
        <CompanyCard
          :fetch-list="fetchCompanies"
          :loading="companiesLoading"
        />
      </div>
      <InfoCard />
    </div>

    <div class="flex flex-row gap-10">
      <div class="flex-1 space-y-5">
        <n-card>
          <div class="flex items-center gap-4">
            <settings-menu :type="'country'" />
            <settings-menu :type="'year'" />
            <geo-restriction class="mt-1" />
            <database-refresh v-show="false" />
          </div>
        </n-card>
        <bound-conditions-list />
        <div class="flex justify-between">
          <AddConditionButton />
          <AssignButton :fetch-assignments="fetchAssignments" />
        </div>
      </div>

      <div class="flex-1 space-y-5">
        <n-card>
          <div class="flex text-xl gap-5 justify-between">
            Gesamtpreis:
            <div class="flex text-xl gap-5">
              <div v-if="totalCosts">
                {{
                  totalCosts.toLocaleString("de-DE", {
                    style: "currency",
                    currency: "EUR",
                    maximumFractionDigits: 2,
                  })
                }}
              </div>
              <n-icon
                v-if="calculateCostDifference(totalCosts, totalCostsConstraints) < 0"
                size="28"
                color="#CE332E"
              >
                <ArrowUp />
              </n-icon>
              <n-icon
                v-if="calculateCostDifference(totalCosts, totalCostsConstraints) > 0"
                size="28"
                color="#00CFB4"
              >
                <ArrowDown />
              </n-icon>
              <n-tag :bordered="false">
                {{
                  calculateCostDifference(totalCosts, totalCostsConstraints) < 0
                    ? "+"
                    : calculateCostDifference(totalCosts, totalCostsConstraints) > 0
                      ? "-"
                      : "+/-"
                }}
                {{ calculateCostDifferenceString(totalCosts, totalCostsConstraints) }}
              </n-tag>
              <n-icon
                v-if="calculateCostDifference(totalCosts, totalCostsConstraints) < 0"
                size="28"
                color="#CE332E"
              >
                <ArrowUp />
              </n-icon>
              <n-icon
                v-if="calculateCostDifference(totalCosts, totalCostsConstraints) > 0"
                size="28"
                color="#00CFB4"
              >
                <ArrowDown />
              </n-icon>
              <div
                v-if="totalCostsConstraints"
                class="text-primary"
              >
                {{
                  totalCostsConstraints.toLocaleString("de-DE", {
                    style: "currency",
                    currency: "EUR",
                    maximumFractionDigits: 2,
                  })
                }}
              </div>
            </div>
          </div>
        </n-card>

        <AreaAssignmentTable
          :assigned-data="assignData"
          :constrained-data="assignDataConstraints"
          :loading="assignLoading || assignConstraintsLoading"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref, watch} from 'vue'
import {ArrowDown, ArrowUp} from '@vicons/ionicons5'
import {useStore} from 'vuex'
import LayoutHeader from '@/layout/LayoutHeader.vue'
import BoundConditionsList from '@/components/bound-conditions/BoundConditionsList.vue'
import AreaCard from '@/components/areas/AreaCard.vue'
import CompanyCard from '@/components/companies/CompanyCard.vue'
import AddConditionButton from '@/components/bound-conditions/AddConditionButton.vue'
import AssignButton from '@/components/assingments/AssignButton.vue'
import AreaAssignmentTable from '@/components/assingments/AreaAssignmentTable.vue'
import InfoCard from '@/components/info/InfoCard.vue'
import DatabaseRefresh from '@/components/header/DatabaseRefresh.vue'
import SettingsMenu from '@/components/settings/SettingsMenu.vue'
import GeoRestriction from '@/components/header/GeoRestriction.vue'

const calculateCostDifference = ref((value1: number, value2: number) => {
  return value1 - value2
})

const calculateCostDifferenceString = ref((value1: number, value2: number) => {
  return `${(Math.abs((value1 - value2) / ((value1 + value2) / 2)) * 100).toFixed(1)} %`
})

const store = useStore()

const assignData = computed(() => store.getters['Assignments/getStandardAssignment'])
const assignDataConstraints = computed(() => store.getters['Assignments/getConstraintAssignment'])
const totalCosts = computed(() => store.getters['Assignments/getStandardTotalCosts'])
const totalCostsConstraints = computed(() => store.getters['Assignments/getConstraintTotalCosts'])

const assignLoading = computed(() => store.getters['Assignments/standardAssignmentLoading'])
const assignConstraintsLoading = computed(
  () => store.getters['Assignments/constraintAssignmentLoading'],
)

const fetchAssignments = () => {
  const geoRestriction = store.getters['Settings/getGeoRestriction']
  store.dispatch('Assignments/FETCH_STANDARD_ASSIGNMENTS', geoRestriction)
  store.dispatch('Assignments/FETCH_CONSTRAINT_ASSIGNMENTS', geoRestriction)
}

const areasLoading = ref(false)

const fetchAreas = () => {
  store.dispatch('Areas/ADD_AREA_LIST')
}

const companiesLoading = ref(false)

const fetchCompanies = () => {
  store.dispatch('Companies/ADD_COMPANY_LIST')
}

watch(
  () => store.getters['Auth/isAuth'],
  (value) => {
    if (value) {
      fetchAreas()
      fetchCompanies()
    }
  },
)

onMounted(() => {
  fetchAreas()
  fetchCompanies()
})
</script>

<style scoped></style>
